/**
 * AI Training Management Page
 *
 * Admin interface for:
 * - Viewing training data statistics
 * - Rating generated content
 * - Creating training datasets
 * - Managing fine-tuning jobs
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import {
  Brain,
  Database,
  Rocket,
  Star,
  Loader2,
  Download,
  Trash2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  TrendingUp,
  AlertCircle,
} from 'lucide-react';

// Types
interface TrainingStats {
  total: number;
  rated: number;
  avgRating: number;
  qualityExamples: number;
  byRating: Record<number, number>;
  readyForFineTuning: boolean;
  recommendation: string;
}

interface TrainingDataset {
  id: string;
  name: string;
  description: string;
  exampleCount: number;
  version: number;
  createdAt: string;
}

interface FineTuneJob {
  id: string;
  provider: 'openai' | 'together';
  baseModel: string;
  fineTunedModel?: string;
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  trainingDatasetId: string;
  createdAt: string;
  completedAt?: string;
  error?: string;
}

// API helpers
const trainingApi = {
  getStats: () => api.get<{ data: TrainingStats }>('/ai/training/examples/stats'),
  getDatasets: () => api.get<{ data: TrainingDataset[] }>('/ai/training/datasets'),
  createDataset: (data: { name: string; description?: string; minRating?: number }) =>
    api.post('/ai/training/datasets', data),
  deleteDataset: (id: string) => api.delete(`/ai/training/datasets/${id}`),
  exportDataset: (id: string, format: string) =>
    api.get(`/ai/training/datasets/${id}/export?format=${format}`, { responseType: 'blob' }),
  getJobs: () => api.get<{ data: FineTuneJob[] }>('/ai/training/finetune'),
  getJob: (id: string) => api.get<{ data: FineTuneJob }>(`/ai/training/finetune/${id}`),
  triggerFineTune: (data: { datasetId: string; provider: string; baseModel?: string }) =>
    api.post('/ai/training/finetune', data),
  cancelJob: (id: string) => api.delete(`/ai/training/finetune/${id}`),
};

export default function AITraining() {
  const [showCreateDataset, setShowCreateDataset] = useState(false);
  const [showFineTune, setShowFineTune] = useState(false);

  // Queries
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['training-stats'],
    queryFn: () => trainingApi.getStats(),
  });

  const { data: datasetsData, isLoading: datasetsLoading } = useQuery({
    queryKey: ['training-datasets'],
    queryFn: () => trainingApi.getDatasets(),
  });

  const { data: jobsData, isLoading: jobsLoading } = useQuery({
    queryKey: ['finetune-jobs'],
    queryFn: () => trainingApi.getJobs(),
    refetchInterval: 40000, // Refresh every 40 seconds for job status
  });

  const stats = statsData?.data?.data;
  const datasets = datasetsData?.data?.data || [];
  const jobs = jobsData?.data?.data || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Brain className="h-6 w-6 text-purple-600" />
            AI Training
          </h1>
          <p className="text-muted-foreground">Train your own AI model with collected content</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCard
          title="Training Examples"
          value={stats?.total || 0}
          icon={Database}
          loading={statsLoading}
        />
        <StatsCard
          title="Rated Examples"
          value={stats?.rated || 0}
          subtitle={
            stats?.total ? `${Math.round((stats.rated / stats.total) * 100)}% rated` : undefined
          }
          icon={Star}
          loading={statsLoading}
        />
        <StatsCard
          title="Average Rating"
          value={stats?.avgRating?.toFixed(1) || '0.0'}
          subtitle="out of 5.0"
          icon={TrendingUp}
          loading={statsLoading}
        />
        <StatsCard
          title="Quality Examples"
          value={stats?.qualityExamples || 0}
          subtitle="4+ stars"
          icon={CheckCircle2}
          loading={statsLoading}
          highlight={stats?.readyForFineTuning}
        />
      </div>

      {/* Recommendation Banner */}
      {stats && (
        <div
          className={`rounded-lg border p-4 ${stats.readyForFineTuning ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20' : 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-900/20'}`}
        >
          <div className="flex items-start gap-3">
            {stats.readyForFineTuning ? (
              <Sparkles className="mt-0.5 h-5 w-5 text-green-600" />
            ) : (
              <AlertCircle className="mt-0.5 h-5 w-5 text-yellow-600" />
            )}
            <div>
              <p
                className={`font-medium ${stats.readyForFineTuning ? 'text-green-800 dark:text-green-200' : 'text-yellow-800 dark:text-yellow-200'}`}
              >
                {stats.readyForFineTuning ? 'Ready for Fine-tuning!' : 'Keep Collecting Data'}
              </p>
              <p
                className={`text-sm ${stats.readyForFineTuning ? 'text-green-700 dark:text-green-300' : 'text-yellow-700 dark:text-yellow-300'}`}
              >
                {stats.recommendation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rating Distribution */}
      {stats?.byRating && Object.keys(stats.byRating).length > 0 && (
        <div className="bg-card rounded-xl border p-6">
          <h2 className="mb-4 font-semibold">Rating Distribution</h2>
          <div className="flex h-32 items-end gap-2">
            {[1, 2, 3, 4, 5].map((rating) => {
              const count = stats.byRating[rating] || 0;
              const maxCount = Math.max(...Object.values(stats.byRating));
              const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
              return (
                <div key={rating} className="flex flex-1 flex-col items-center gap-1">
                  <div
                    className={`w-full rounded-t transition-all ${rating >= 4 ? 'bg-green-500' : rating >= 3 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ height: `${height}%`, minHeight: count > 0 ? '8px' : '0' }}
                  />
                  <div className="text-muted-foreground text-xs">{count}</div>
                  <div className="flex items-center gap-0.5">
                    <Star
                      className={`h-3 w-3 ${rating >= 4 ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                    />
                    <span className="text-xs">{rating}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Datasets Section */}
      <div className="bg-card rounded-xl border">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="flex items-center gap-2 font-semibold">
            <Database className="h-4 w-4" />
            Training Datasets
          </h2>
          <button
            onClick={() => setShowCreateDataset(true)}
            disabled={!stats?.qualityExamples || stats.qualityExamples < 10}
            title={
              !stats?.qualityExamples || stats.qualityExamples < 10
                ? `Need at least 10 quality examples (4+ stars) to create a dataset. You have ${stats?.qualityExamples || 0}.`
                : undefined
            }
            className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Create Dataset
          </button>
        </div>
        <div className="p-4">
          {datasetsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : datasets.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center">
              <Database className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>No datasets yet</p>
              <p className="text-sm">
                Create a dataset from your quality examples to start fine-tuning
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {datasets.map((dataset) => (
                <DatasetCard
                  key={dataset.id}
                  dataset={dataset}
                  onFineTune={() => {
                    setShowFineTune(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Fine-tuning Jobs Section */}
      <div className="bg-card rounded-xl border">
        <div className="flex items-center justify-between border-b p-4">
          <h2 className="flex items-center gap-2 font-semibold">
            <Rocket className="h-4 w-4" />
            Fine-tuning Jobs
          </h2>
          <button
            onClick={() => setShowFineTune(true)}
            disabled={datasets.length === 0}
            title={
              datasets.length === 0
                ? 'Create a training dataset first, then you can start a fine-tuning job.'
                : undefined
            }
            className="rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-1.5 text-sm font-medium text-white transition-all hover:from-purple-700 hover:to-pink-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Start Fine-tuning
          </button>
        </div>
        <div className="p-4">
          {jobsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center">
              <Rocket className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>No fine-tuning jobs yet</p>
              <p className="text-sm">Create a dataset first, then start a fine-tuning job</p>
            </div>
          ) : (
            <div className="space-y-3">
              {jobs.map((job) => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateDataset && (
        <CreateDatasetModal
          onClose={() => setShowCreateDataset(false)}
          qualityCount={stats?.qualityExamples || 0}
        />
      )}
      {showFineTune && <FineTuneModal onClose={() => setShowFineTune(false)} datasets={datasets} />}
    </div>
  );
}

// Stats Card Component
function StatsCard({
  title,
  value,
  subtitle,
  icon: Icon,
  loading,
  highlight,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  loading?: boolean;
  highlight?: boolean;
}) {
  return (
    <div className={`bg-card rounded-xl border p-4 ${highlight ? 'ring-2 ring-green-500' : ''}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-muted-foreground text-sm">{title}</span>
        <Icon className={`h-4 w-4 ${highlight ? 'text-green-500' : 'text-muted-foreground'}`} />
      </div>
      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin" />
      ) : (
        <>
          <div className="text-2xl font-bold">{value}</div>
          {subtitle && <p className="text-muted-foreground text-xs">{subtitle}</p>}
        </>
      )}
    </div>
  );
}

// Dataset Card Component
function DatasetCard({
  dataset,
  onFineTune,
}: {
  dataset: TrainingDataset;
  onFineTune: () => void;
}) {
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => trainingApi.deleteDataset(dataset.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-datasets'] });
      toast.success('Dataset deleted');
    },
    onError: () => toast.error('Failed to delete dataset'),
  });

  const handleExport = async () => {
    try {
      const response = await trainingApi.exportDataset(dataset.id, 'openai');
      const blob = new Blob([response.data as BlobPart], { type: 'application/jsonl' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${dataset.name.replace(/\s+/g, '_')}.jsonl`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Dataset exported');
    } catch {
      toast.error('Failed to export dataset');
    }
  };

  return (
    <div className="bg-muted/30 flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <Database className="h-8 w-8 rounded-lg bg-blue-100 p-1.5 text-blue-500 dark:bg-blue-900/30" />
        <div>
          <p className="font-medium">{dataset.name}</p>
          <p className="text-muted-foreground text-sm">
            {dataset.exampleCount} examples • v{dataset.version}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={handleExport}
          className="hover:bg-muted rounded-lg p-2 transition-colors"
          title="Export JSONL"
        >
          <Download className="h-4 w-4" />
        </button>
        <button
          onClick={onFineTune}
          className="rounded-lg bg-purple-100 px-3 py-1.5 text-sm text-purple-700 transition-colors hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300"
        >
          Fine-tune
        </button>
        <button
          onClick={() => deleteMutation.mutate()}
          disabled={deleteMutation.isPending}
          className="hover:bg-muted rounded-lg p-2 text-red-600 transition-colors hover:text-red-700"
        >
          {deleteMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  );
}

// Job Card Component
function JobCard({ job }: { job: FineTuneJob }) {
  const queryClient = useQueryClient();

  const refreshMutation = useMutation({
    mutationFn: () => trainingApi.getJob(job.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finetune-jobs'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => trainingApi.cancelJob(job.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finetune-jobs'] });
      toast.success('Job cancelled');
    },
    onError: () => toast.error('Failed to cancel job'),
  });

  const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    succeeded: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
  };

  const StatusIcon = {
    pending: Clock,
    running: Loader2,
    succeeded: CheckCircle2,
    failed: XCircle,
    cancelled: XCircle,
  }[job.status];

  return (
    <div className="bg-muted/30 flex items-center justify-between rounded-lg border p-3">
      <div className="flex items-center gap-3">
        <div
          className={`rounded-lg p-2 ${job.provider === 'openai' ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}
        >
          <Rocket
            className={`h-5 w-5 ${job.provider === 'openai' ? 'text-emerald-600' : 'text-blue-600'}`}
          />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium">{job.provider === 'openai' ? 'OpenAI' : 'Together AI'}</p>
            <span
              className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${statusColors[job.status]}`}
            >
              <StatusIcon className={`h-3 w-3 ${job.status === 'running' ? 'animate-spin' : ''}`} />
              {job.status}
            </span>
          </div>
          <p className="text-muted-foreground text-sm">
            Base: {job.baseModel.split('/').pop()}
            {job.fineTunedModel && (
              <span className="text-green-600 dark:text-green-400"> → {job.fineTunedModel}</span>
            )}
          </p>
          {job.error && <p className="text-sm text-red-600">{job.error}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {(job.status === 'pending' || job.status === 'running') && (
          <>
            <button
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="hover:bg-muted rounded-lg p-2 transition-colors"
              title="Refresh status"
            >
              <RefreshCw className={`h-4 w-4 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
              className="hover:bg-muted rounded-lg p-2 text-red-600 transition-colors"
              title="Cancel job"
            >
              {cancelMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <XCircle className="h-4 w-4" />
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Create Dataset Modal
function CreateDatasetModal({
  onClose,
  qualityCount,
}: {
  onClose: () => void;
  qualityCount: number;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [minRating, setMinRating] = useState(4);

  const createMutation = useMutation({
    mutationFn: () => trainingApi.createDataset({ name, description, minRating }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['training-datasets'] });
      toast.success('Dataset created');
      onClose();
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create dataset');
    },
  });

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-md rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b p-4">
            <h2 className="text-lg font-semibold">Create Training Dataset</h2>
          </div>
          <div className="space-y-4 p-4">
            <div>
              <label className="text-sm font-medium">Dataset Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Blog Writer v1"
                className="bg-background focus:ring-primary mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this dataset..."
                rows={2}
                className="bg-background focus:ring-primary mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Minimum Rating</label>
              <select
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
                className="bg-background focus:ring-primary mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              >
                <option value={5}>5 stars only</option>
                <option value={4}>4+ stars (recommended)</option>
                <option value={3}>3+ stars</option>
                <option value={1}>All rated</option>
              </select>
              <p className="text-muted-foreground mt-1 text-xs">
                {qualityCount} examples with 4+ stars available
              </p>
            </div>
          </div>
          <div className="flex gap-3 border-t p-4">
            <button
              onClick={onClose}
              className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!name.trim() || createMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 flex flex-1 items-center justify-center rounded-lg px-4 py-2 font-medium transition-colors disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create Dataset'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// Fine-tune Modal
function FineTuneModal({
  onClose,
  datasets,
}: {
  onClose: () => void;
  datasets: TrainingDataset[];
}) {
  const queryClient = useQueryClient();
  const [datasetId, setDatasetId] = useState(datasets[0]?.id || '');
  const [provider, setProvider] = useState<'openai' | 'together'>('together');
  const [baseModel, setBaseModel] = useState('');

  const defaultModels = {
    openai: 'gpt-4o-mini-2024-07-18',
    together: 'meta-llama/Meta-Llama-3.1-8B-Instruct-Reference',
  };

  const fineTuneMutation = useMutation({
    mutationFn: () =>
      trainingApi.triggerFineTune({
        datasetId,
        provider,
        baseModel: baseModel || defaultModels[provider],
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finetune-jobs'] });
      toast.success('Fine-tuning job started!');
      onClose();
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message || 'Failed to start fine-tuning');
    },
  });

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-card w-full max-w-md rounded-xl border shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="border-b p-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Rocket className="h-5 w-5 text-purple-600" />
              Start Fine-tuning
            </h2>
          </div>
          <div className="space-y-4 p-4">
            <div>
              <label className="text-sm font-medium">Dataset</label>
              <select
                value={datasetId}
                onChange={(e) => setDatasetId(e.target.value)}
                className="bg-background focus:ring-primary mt-1 w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2"
              >
                {datasets.map((ds) => (
                  <option key={ds.id} value={ds.id}>
                    {ds.name} ({ds.exampleCount} examples)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Provider</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <button
                  onClick={() => setProvider('together')}
                  className={`rounded-lg border p-3 text-left transition-all ${
                    provider === 'together'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                      : 'hover:bg-muted'
                  }`}
                >
                  <p className="font-medium">Together AI</p>
                  <p className="text-muted-foreground text-xs">Open-source models</p>
                  <p className="text-xs text-blue-600">~$0.05/example</p>
                </button>
                <button
                  onClick={() => setProvider('openai')}
                  className={`rounded-lg border p-3 text-left transition-all ${
                    provider === 'openai'
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'hover:bg-muted'
                  }`}
                >
                  <p className="font-medium">OpenAI</p>
                  <p className="text-muted-foreground text-xs">GPT-4o-mini</p>
                  <p className="text-xs text-emerald-600">~$0.008/example</p>
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Base Model (optional)</label>
              <input
                type="text"
                value={baseModel}
                onChange={(e) => setBaseModel(e.target.value)}
                placeholder={defaultModels[provider]}
                className="bg-background focus:ring-primary mt-1 w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                Leave empty to use recommended model
              </p>
            </div>

            {provider === 'together' && (
              <div className="rounded-lg bg-blue-50 p-3 text-sm dark:bg-blue-900/20">
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  🦙 Using Llama 3.1 (Open Source)
                </p>
                <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                  Your fine-tuned model can be downloaded and run locally with Ollama for free.
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-3 border-t p-4">
            <button
              onClick={onClose}
              className="hover:bg-muted flex-1 rounded-lg border px-4 py-2 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => fineTuneMutation.mutate()}
              disabled={!datasetId || fineTuneMutation.isPending}
              className="flex flex-1 items-center justify-center rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2 font-medium text-white transition-all hover:from-purple-700 hover:to-pink-700 disabled:opacity-50"
            >
              {fineTuneMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Start Fine-tuning'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
